import React, { useEffect, useState } from 'react'
import { leaV3API, SamplePlan, PlanStep } from '../api/leaV3.api'

export const DryRunView: React.FC = () => {
  const [plans, setPlans] = useState<SamplePlan[]>([])
  const [note, setNote] = useState<string | undefined>()
  const [generatedAt, setGeneratedAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    leaV3API
      .getSamplePlans()
      .then((r) => {
        setPlans(r.plans || [])
        setNote(r.note)
        setGeneratedAt(r.generated_at)
      })
      .catch((e) => setError(e?.message || 'Failed to load sample plans'))
      .finally(() => setLoading(false))
  }, [])

  if (loading) return <div className="p-4 text-gray-500">Chargement…</div>
  if (error) return <div className="p-4 text-red-600">Erreur : {error}</div>

  return (
    <div className="space-y-6">
      <div className="bg-blue-50 border border-blue-200 text-blue-900 px-4 py-3 rounded">
        <div className="font-semibold mb-1">Dry-Run — plans préprocessés</div>
        <div className="text-sm">
          Aperçu de ce que Claude proposerait pour 2 affaires expirées sélectionnées.
          <strong> Aucun appel API n'a été émis</strong> — les outputs ci-dessous sont des plans
          écrits à la main pour valider le format avant de brancher Anthropic.
        </div>
        {generatedAt && (
          <div className="text-xs text-blue-700 mt-1">Généré le {new Date(generatedAt).toLocaleString('fr-BE')}</div>
        )}
        {note && <div className="text-xs italic text-blue-700 mt-1">{note}</div>}
      </div>

      {plans.length === 0 ? (
        <div className="p-4 text-gray-500">Pas de plans dry-run disponibles.</div>
      ) : (
        plans.map((plan) => <PlanCard key={plan.id} plan={plan} />)
      )}
    </div>
  )
}

const PlanCard: React.FC<{ plan: SamplePlan }> = ({ plan }) => {
  const [showPromptMeta, setShowPromptMeta] = useState(false)
  const [fullPrompt, setFullPrompt] = useState<string | null>(null)
  const [loadingPrompt, setLoadingPrompt] = useState(false)

  const loadPrompt = async () => {
    if (fullPrompt !== null) {
      setFullPrompt(null) // toggle close
      return
    }
    setLoadingPrompt(true)
    try {
      const body = await leaV3API.getSamplePlanPrompt(plan.id)
      setFullPrompt(body)
    } catch (e) {
      setFullPrompt(`<<error loading prompt: ${(e as any)?.message || 'unknown'}>>`)
    } finally {
      setLoadingPrompt(false)
    }
  }

  return (
    <article className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      <header className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex items-center gap-4">
        <div>
          <div className="font-semibold text-gray-800">
            Affaire #{plan.affaire.affaire_id}
            <span className="ml-2 text-xs font-normal text-gray-500">{plan.affaire.status}</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            Trigger : <span className="font-mono">{plan.trigger.type}</span> /{' '}
            <span className="font-mono">{plan.trigger.category}</span>
            {plan.trigger.ladder_slot && (
              <>
                {' '}/ <span className="font-mono">{plan.trigger.ladder_slot}</span>
              </>
            )}
            {' • '}fired {new Date(plan.trigger.fired_at).toLocaleString('fr-BE')}
          </div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-xs text-gray-500">Route matchée</div>
          <div className="font-mono font-semibold text-[#ef5343]">{plan.matched_route.id}</div>
          <div className="text-xs text-gray-500 mt-0.5">delay : {plan.matched_route.process_delay}</div>
        </div>
      </header>

      <div className="p-4 space-y-4">
        <section>
          <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Analysis</h4>
          <p className="text-sm text-gray-800 leading-relaxed">{plan.claude_output.analysis}</p>
        </section>

        <section>
          <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-1">Recommendation</h4>
          <p className="text-sm text-gray-800 leading-relaxed">{plan.claude_output.recommendation}</p>
        </section>

        <section>
          <h4 className="text-xs uppercase tracking-wide text-gray-500 mb-2">
            Steps ({plan.claude_output.steps.length})
          </h4>
          <div className="space-y-2">
            {plan.claude_output.steps.map((step, i) => (
              <StepCard key={i} step={step} index={i + 1} />
            ))}
          </div>
        </section>

        <section className="pt-2 border-t border-gray-100 space-y-2">
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={() => setShowPromptMeta(!showPromptMeta)}
              className="text-xs text-gray-500 hover:text-gray-800"
            >
              {showPromptMeta ? '▼' : '▶'} Prompt meta ({plan.prompt_meta.total_chars.toLocaleString('fr-BE')} chars, {plan.prompt_meta.knowledge_layers.length} layers, {plan.prompt_meta.dossier_thread_count} threads)
            </button>
            <button
              onClick={loadPrompt}
              className="text-xs px-2 py-0.5 bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded font-mono"
              disabled={loadingPrompt}
            >
              {loadingPrompt ? '…' : fullPrompt === null ? '👁 Show full prompt' : '✕ Hide full prompt'}
            </button>
          </div>
          {showPromptMeta && (
            <div className="text-xs bg-gray-50 border border-gray-200 rounded p-3">
              <div className="font-mono text-gray-700">
                Knowledge layers composées :
                <ul className="mt-1 space-y-0.5">
                  {plan.prompt_meta.knowledge_layers.map((l) => (
                    <li key={l}>
                      • <span className="text-[#ef5343]">{l}</span>
                    </li>
                  ))}
                </ul>
              </div>
              {plan.claude_output.estimated_token_usage && (
                <div className="mt-2 text-gray-600">
                  Tokens estimés : input ≈ {plan.claude_output.estimated_token_usage.input.toLocaleString('fr-BE')},
                  output ≈ {plan.claude_output.estimated_token_usage.output.toLocaleString('fr-BE')}
                </div>
              )}
            </div>
          )}
          {fullPrompt !== null && (
            <div className="bg-gray-900 text-gray-100 rounded border border-gray-700 max-h-[600px] overflow-auto">
              <div className="sticky top-0 bg-gray-800 px-3 py-1.5 border-b border-gray-700 text-xs flex items-center gap-3">
                <span className="font-mono">prompt_{plan.id}.md</span>
                <span className="text-gray-400">{fullPrompt.length.toLocaleString('fr-BE')} chars</span>
                <button
                  onClick={() => navigator.clipboard.writeText(fullPrompt)}
                  className="ml-auto px-2 py-0.5 bg-gray-700 hover:bg-gray-600 rounded text-xs"
                >
                  Copy
                </button>
              </div>
              <pre className="px-3 py-2 text-xs whitespace-pre-wrap font-mono leading-relaxed">
                {fullPrompt}
              </pre>
            </div>
          )}
        </section>
      </div>
    </article>
  )
}

const StepCard: React.FC<{ step: PlanStep; index: number }> = ({ step, index }) => {
  const c = step.content as Record<string, string | undefined>
  const colorClass =
    step.type === 'email'
      ? 'bg-purple-50 border-purple-200 text-purple-800'
      : step.type === 'tracker_com'
      ? 'bg-amber-50 border-amber-200 text-amber-900'
      : 'bg-green-50 border-green-200 text-green-800'

  return (
    <div className={`border rounded px-3 py-2 ${colorClass}`}>
      <div className="flex items-baseline gap-2 mb-2">
        <span className="text-xs font-semibold uppercase">#{index} · {step.type}</span>
        {c.to && <span className="text-xs">→ {c.to}</span>}
        {c.assign_to && <span className="text-xs">→ {c.assign_to}</span>}
      </div>
      {c.subject && (
        <div className="text-sm font-semibold mb-1">{c.subject}</div>
      )}
      {(c.body || c.message) && (
        <pre className="text-xs whitespace-pre-wrap font-sans text-gray-800">
          {c.body || c.message}
        </pre>
      )}
    </div>
  )
}
